import clsx from 'clsx';

/**
 * Table - Reusable, responsive tablo bileşeni
 * 
 * @param {Array} columns - Sütun tanımları [{key, header, width, align, render}]
 * @param {Array} data - Tablo verileri
 * @param {boolean} striped - Zebra çizgili satırlar
 * @param {boolean} hover - Hover efekti
 * @param {boolean} showRowNumbers - Satır numarası göster (#)
 * @param {number} rowNumberOffset - Satır numarası başlangıç offset'i (pagination için)
 * @param {function} onRowClick - Satır tıklama
 * @param {string} emptyMessage - Boş durum mesajı
 * @param {ReactNode} emptyIcon - Boş durum ikonu
 */
export function Table({
    columns = [],
    data = [],
    striped = true,
    hover = true,
    bordered = false,
    compact = false,
    showRowNumbers = false,
    rowNumberOffset = 0,
    onRowClick,
    emptyMessage = 'Veri bulunamadı.',
    emptyIcon,
    className = '',
    headerClassName = '',
    rowClassName = '',
}) {

    // Responsive genişlik sınıfları
    const getColumnWidth = (width) => {
        if (!width) return '';

        const widthClasses = {
            xs: 'w-16 min-w-[4rem]',
            sm: 'w-24 min-w-[6rem]',
            md: 'w-32 min-w-[8rem]',
            lg: 'w-48 min-w-[12rem]',
            xl: 'w-64 min-w-[16rem]',
            auto: 'w-auto',
            full: 'flex-1',
        };

        return widthClasses[width] || width;
    };

    const getAlignment = (align) => {
        const alignClasses = {
            left: 'text-left',
            center: 'text-center',
            right: 'text-right',
        };
        return alignClasses[align] || 'text-left';
    };

    return (
        <div className={clsx('w-full', className)}>
            {/* Scrollable container */}
            <div className="overflow-x-auto rounded-lg border border-border-light">
                <table className="w-full min-w-max">
                    {/* Header */}
                    <thead className={clsx('bg-bg-tertiary', headerClassName)}>
                        <tr>
                            {/* Row Number Header */}
                            {showRowNumbers && (
                                <th
                                    className={clsx(
                                        'px-3 font-semibold text-text-secondary whitespace-nowrap text-center',
                                        compact ? 'py-2 text-xs' : 'py-3 text-sm',
                                        'w-12 min-w-[3rem]',
                                        bordered && 'border-r border-border-light'
                                    )}
                                >
                                    #
                                </th>
                            )}
                            {columns.map((col, idx) => (
                                <th
                                    key={col.key || idx}
                                    className={clsx(
                                        'px-4 font-semibold text-text-secondary whitespace-nowrap',
                                        compact ? 'py-2 text-xs' : 'py-3 text-sm',
                                        getColumnWidth(col.width),
                                        getAlignment(col.align),
                                        bordered && 'border-r border-border-light last:border-r-0'
                                    )}
                                >
                                    {col.header}
                                </th>
                            ))}
                        </tr>
                    </thead>


                    {/* Body */}
                    <tbody>
                        {data.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={showRowNumbers ? columns.length + 1 : columns.length}
                                    className="px-4 py-12 text-center text-text-tertiary"
                                >
                                    <div className="flex flex-col items-center gap-2">
                                        {emptyIcon && (
                                            <span className="opacity-50">{emptyIcon}</span>
                                        )}
                                        <span>{emptyMessage}</span>
                                    </div>
                                </td>
                            </tr>

                        ) : (
                            data.map((row, rowIdx) => (
                                <tr
                                    key={row.id || rowIdx}
                                    onClick={() => onRowClick?.(row)}
                                    className={clsx(
                                        'border-t border-border-light transition-colors',
                                        striped && rowIdx % 2 === 1 && 'bg-bg-primary',
                                        hover && 'hover:bg-bg-hover',
                                        onRowClick && 'cursor-pointer',
                                        rowClassName
                                    )}
                                >
                                    {/* Row Number Cell */}
                                    {showRowNumbers && (
                                        <td
                                            className={clsx(
                                                'px-3 text-text-muted font-medium text-center tabular-nums',
                                                compact ? 'py-2 text-xs' : 'py-3 text-sm',
                                                'w-12 min-w-[3rem]',
                                                bordered && 'border-r border-border-light'
                                            )}
                                        >
                                            {rowNumberOffset + rowIdx + 1}
                                        </td>
                                    )}
                                    {columns.map((col, colIdx) => (
                                        <td
                                            key={col.key || colIdx}
                                            className={clsx(
                                                'px-4 text-text-primary whitespace-nowrap',
                                                compact ? 'py-2 text-sm' : 'py-3',
                                                getColumnWidth(col.width),
                                                getAlignment(col.align),
                                                bordered && 'border-r border-border-light last:border-r-0'
                                            )}
                                        >
                                            {col.render
                                                ? col.render(row[col.key], row, rowIdx)
                                                : row[col.key]
                                            }
                                        </td>
                                    ))}
                                </tr>

                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/**
 * TableHeader - Tablo üst kısmı (başlık + aksiyonlar)
 */
export function TableHeader({
    title,
    subtitle,
    children, // Action buttons
    className = '',
}) {
    return (
        <div className={clsx(
            'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4',
            className
        )}>
            <div>
                {title && <h2 className="text-lg font-semibold text-text-primary">{title}</h2>}
                {subtitle && <p className="text-sm text-text-tertiary mt-0.5">{subtitle}</p>}
            </div>
            {children && (
                <div className="flex flex-wrap items-center gap-2">
                    {children}
                </div>
            )}
        </div>
    );
}

/**
 * StatusBadge - Durum etiketi
 */
export function StatusBadge({ status, variant = 'default', size = 'sm' }) {
    const variantClasses = {
        default: 'bg-neutral-100 text-neutral-700',
        primary: 'bg-primary-100 text-primary-dark',
        secondary: 'bg-secondary-100 text-secondary-dark',
        tertiary: 'bg-tertiary-100 text-tertiary-dark',
        success: 'bg-success-100 text-success-dark',
        warning: 'bg-warning-100 text-warning-dark',
        error: 'bg-error-100 text-error-dark',
        info: 'bg-info-100 text-info-dark',
    };

    const sizeClasses = {
        xs: 'px-1.5 py-0.5 text-2xs',
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-sm',
    };

    return (
        <span className={clsx(
            'inline-flex items-center font-medium rounded-full',
            variantClasses[variant],
            sizeClasses[size]
        )}>
            {status}
        </span>
    );
}

/**
 * ActionCell - Tablo aksiyon hücresi
 */
export function ActionCell({ children, className = '' }) {
    return (
        <div className={clsx('flex items-center gap-1', className)}>
            {children}
        </div>
    );
}

export default Table;
